import { DestroyRef, Directive, ElementRef, afterNextRender, inject } from '@angular/core';

/** How long each position is held before the row moves on. */
const STEP_INTERVAL_MS = 3000;

/**
 * Makes a sideways-scrolling row move on by itself, one item at a time.
 *
 * Written for the "{Event} Top Stores" logo strip. That strip is a CSS scroller with its scrollbar
 * hidden and no arrows, which was invisible as a problem while an event listed eight stores,
 * because eight fit. With twenty, half of them sat off the right edge and a mouse had no way to
 * reach them: the row looked like a carousel that had stopped.
 *
 * It only moves the viewport. Every item stays in the DOM, so the store links the strip exists for
 * are all still in the server-rendered HTML; see the note on `.sav-top-store-list` in styles.scss.
 *
 * Nothing happens when the items fit, when the visitor asks for reduced motion, while the pointer
 * or keyboard focus is on the row, or while the tab is in the background. After the last item it
 * returns to the first.
 */
@Directive({
  selector: '[appAutoScrollRow]',
  host: {
    '(pointerenter)': 'paused = true',
    '(pointerleave)': 'paused = false',
    '(pointercancel)': 'paused = false',
    '(focusin)': 'paused = true',
    '(focusout)': 'paused = false',
  },
})
export class AutoScrollRow {
  private readonly row = inject<ElementRef<HTMLElement>>(ElementRef).nativeElement;
  private timer: ReturnType<typeof setInterval> | null = null;

  protected paused = false;

  constructor() {
    // Browser only: afterNextRender never runs during server rendering.
    afterNextRender(() => this.start());
    inject(DestroyRef).onDestroy(() => this.stop());
  }

  private start(): void {
    if (this.timer !== null || this.prefersReducedMotion()) {
      return;
    }

    this.timer = setInterval(() => this.step(), STEP_INTERVAL_MS);
  }

  private stop(): void {
    if (this.timer === null) {
      return;
    }

    clearInterval(this.timer);
    this.timer = null;
  }

  // Measured on every step rather than once, because the items arrive after the API answers and
  // the row's width changes with the window.
  private step(): void {
    if (this.paused || document.hidden) {
      return;
    }

    const row = this.row;
    const furthest = row.scrollWidth - row.clientWidth;

    if (furthest <= 1) {
      return;
    }

    const atEnd = row.scrollLeft >= furthest - 1;
    const left = atEnd ? 0 : Math.min(row.scrollLeft + this.itemStride(), furthest);

    row.scrollTo({ left, behavior: 'smooth' });
  }

  /** One item plus the gap after it. Half the row when there is no item to measure. */
  private itemStride(): number {
    const first = this.row.firstElementChild;
    const width = first?.getBoundingClientRect().width ?? 0;

    if (width <= 0) {
      return this.row.clientWidth / 2;
    }

    const gap = Number.parseFloat(getComputedStyle(this.row).columnGap);

    return width + (Number.isFinite(gap) ? gap : 0);
  }

  private prefersReducedMotion(): boolean {
    return (
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
    );
  }
}
